// ── Short-TTL read cache + in-flight dedup ──────────────────────────────────
// Hot, session-stable GETs (workflows, versions, core-diagram) were refetched
// redundantly: an editor and its children pull the SAME core-diagram on mount
// (Editor.js + Toolbar both call fetchCoreDiagram for one versionId), and the
// workflow/version lists are refetched on every modal/tab mount. This caches
// the in-flight/just-resolved promise per key for a few seconds, collapsing
// those bursts into one request. The short TTL is also a safety net: any
// mutation we forget to invalidate self-heals within READ_CACHE_TTL_MS.
//
// Callers that pass an AbortController `signal` BYPASS this cache (see the
// fetch* wrappers) so per-caller cancellation and useEditorRoute's
// fetchIdRef stale-response handling are never altered.
//
// Extracted from api.js (B6/S1a) so api.js and workflowApi.js share exactly
// ONE cache Map instance.
const _readCache = new Map(); // key -> { ts, promise }
const READ_CACHE_TTL_MS = 5000;

export const dedupedRead = (key, run) => {
  const hit = _readCache.get(key);
  if (hit && Date.now() - hit.ts < READ_CACHE_TTL_MS) return hit.promise;
  const promise = Promise.resolve()
    .then(run)
    .catch((err) => {
      _readCache.delete(key); // never cache a failure
      throw err;
    });
  _readCache.set(key, { ts: Date.now(), promise });
  return promise;
};

// Drop cache entries by exact key, `${str}|`-prefix, or predicate. Mutations
// that change cached data call this; broad clears are fine (force a refetch).
export const invalidateRead = (match) => {
  if (match === undefined) {
    _readCache.clear();
    return;
  }
  const test =
    typeof match === "function"
      ? match
      : (k) => k === match || k.startsWith(`${match}|`);
  for (const k of _readCache.keys()) if (test(k)) _readCache.delete(k);
};
